import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTestSet, addTestCase, getPrompts, runTests, getAvailableModels, getTestRunHistory } from '../services/api';
import EvaluationSettings from '../components/EvaluationSettings';
import { ArrowLeft, Plus, Play, Settings, FileText, Calendar, Target, CheckCircle, XCircle, Clock, AlertTriangle, Eye } from 'lucide-react';

const TestSetDetail = () => {
  const { projectId, testSetId } = useParams();
  const navigate = useNavigate();
  
  const [testSet, setTestSet] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [models, setModels] = useState([]);
  const [testRunHistory, setTestRunHistory] = useState([]);
  const [selectedModel, setSelectedModel] = useState('llama-3.1-8b-instant');
  const [selectedTestCases, setSelectedTestCases] = useState([]);
  const [selectAllTests, setSelectAllTests] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showAddCaseForm, setShowAddCaseForm] = useState(false);
  const [showRunTestsModal, setShowRunTestsModal] = useState(false);
  const [showEvaluationSettings, setShowEvaluationSettings] = useState(false);
  const [runningTests, setRunningTests] = useState(false);
  const [newTestCase, setNewTestCase] = useState({
    inputText: '',
    expectedBehavior: '',
    tags: []
  });

  useEffect(() => {
    loadData();
  }, [testSetId, projectId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [testSetData, promptsData, modelsData, historyData] = await Promise.all([
        getTestSet(testSetId),
        getPrompts(projectId),
        getAvailableModels(),
        getTestRunHistory(testSetId)
      ]);
      setTestSet(testSetData);
      setPrompts(promptsData);
      setModels(modelsData);
      setTestRunHistory(historyData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTestCase = async (e) => {
    e.preventDefault();
    try {
      await addTestCase(testSetId, newTestCase);
      setNewTestCase({ inputText: '', expectedBehavior: '', tags: [] });
      setShowAddCaseForm(false);
      loadData();
    } catch (error) {
      console.error('Error adding test case:', error);
    }
  };

  const handleRunTests = async (promptId, versionId) => {
    try {
      setRunningTests(true);
      const testCasesToRun = selectAllTests ? [] : selectedTestCases;
      await runTests(testSetId, versionId, selectedModel, testCasesToRun);
      setShowRunTestsModal(false);
      
      // Reload data to update history
      await loadData();
      
      // Navigate to evaluation page
      navigate(`/projects/${projectId}/test-sets/${testSetId}/evaluate/${versionId}`);
    } catch (error) {
      console.error('Error running tests:', error);
    } finally {
      setRunningTests(false);
    }
  };

  const handleTestCaseSelection = (testCaseId, isSelected) => {
    if (isSelected) {
      setSelectedTestCases(prev => [...prev, testCaseId]);
    } else {
      setSelectedTestCases(prev => prev.filter(id => id !== testCaseId));
    }
  };

  const handleSelectAllToggle = () => {
    setSelectAllTests(!selectAllTests);
    if (!selectAllTests) {
      setSelectedTestCases([]);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner h-12 w-12 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading test set...</p>
        </div>
      </div>
    );
  }

  if (!testSet) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Test Set Not Found</h2>
          <button
            onClick={() => navigate(`/projects/${projectId}/test-sets`)}
            className="btn-primary"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Test Sets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="card border-x-0 border-t-0 mb-8 rounded-2xl">
        <div className="gradient-header rounded-2xl">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-center justify-between py-6">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate(`/projects/${projectId}/test-sets`)}
                  className="btn-ghost p-2 h-auto w-auto"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-celadon-100 rounded-xl">
                    <FileText className="h-6 w-6 text-celadon-700" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                      {testSet.name}
                    </h1>
                    {testSet.description && (
                      <p className="text-gray-600 mt-1">
                        {testSet.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowAddCaseForm(true)}
                  className="btn-secondary"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Test Case
                </button>
                <button
                  onClick={() => setShowRunTestsModal(true)}
                  className="btn-primary"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Run Tests
                </button>
                <button
                  onClick={() => setShowEvaluationSettings(true)}
                  className="btn-ghost"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Evaluation Criteria
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pb-8">
        {testSet.test_cases?.length === 0 ? (
          <div className="card">
            <div className="card-body text-center py-16">
              <div className="mx-auto w-24 h-24 bg-celadon-100 rounded-full flex items-center justify-center mb-6">
                <FileText className="w-12 h-12 text-celadon-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">No test cases yet</h3>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                Add test cases to start evaluating prompt performance and ensure consistent quality.
              </p>
              <button
                onClick={() => setShowAddCaseForm(true)}
                className="btn-primary btn-lg"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add First Test Case
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {testSet.test_cases.map((testCase, index) => (
              <div key={testCase.id} className="card hover:shadow-medium transition-all duration-200">
                <div className="card-body">
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-celadon-100 rounded-lg">
                        <Target className="h-5 w-5 text-celadon-700" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          Test Case #{index + 1}
                        </h3>
                        <div className="flex items-center space-x-2 mt-1">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-500">
                            {new Date(testCase.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        Input Text
                      </label>
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                        <pre className="whitespace-pre-wrap text-sm text-gray-900 font-mono">
                          {testCase.input_text}
                        </pre>
                      </div>
                    </div>
                    
                    {testCase.expected_behavior && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-3">
                          Expected Behavior
                        </label>
                        <div className="bg-celadon-50 rounded-xl p-4 border border-celadon-200">
                          <pre className="whitespace-pre-wrap text-sm text-celadon-800">
                            {testCase.expected_behavior}
                          </pre>
                        </div>
                      </div>
                    )}
                    
                    {testCase.tags && testCase.tags.length > 0 && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-3">
                          Tags
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {testCase.tags.map((tag, tagIndex) => (
                            <span
                              key={tagIndex}
                              className="badge-secondary"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Test Results Section */}
        {testRunHistory.length > 0 && (
          <div className="mt-12">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-celadon-100 rounded-xl">
                <CheckCircle className="h-6 w-6 text-celadon-700" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Test Results History</h2>
            </div>
            <div className="card">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Prompt & Version
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Model
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Test Cases
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Results
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {testRunHistory.map((run, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-semibold text-gray-900">
                              {run.prompt_name}
                            </div>
                            <div className="text-sm text-gray-500">
                              v{run.version_number}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="badge-primary">
                            {run.model_name || 'Unknown'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {run.total_runs} test cases
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-2">
                            {/* Auto-evaluation status */}
                            <div className="flex items-center space-x-2">
                              {run.passed_tests > 0 && (
                                <span className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-celadon-100 text-celadon-800">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  {run.passed_tests} passed
                                </span>
                              )}
                              {run.failed_tests > 0 && (
                                <span className="badge-status-error">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  {run.failed_tests} failed
                                </span>
                              )}
                              {run.pending_tests > 0 && (
                                <span className="badge-status-warning">
                                  <Clock className="w-3 h-3 mr-1 animate-spin" />
                                  {run.pending_tests} pending
                                </span>
                              )}
                              {run.error_tests > 0 && (
                                <span className="badge-status-info">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  {run.error_tests} errors
                                </span>
                              )}
                            </div>
                            
                            {/* Manual evaluation */}
                            <div className="text-sm text-gray-600">
                              Manual: {run.evaluated_runs}/{run.total_runs} evaluated
                              {run.avg_score !== null && (
                                <span className="ml-2 font-medium">
                                  (Avg: {parseFloat(run.avg_score).toFixed(1)})
                                </span>
                              )}
                            </div>
                            
                            {/* Auto-evaluation score */}
                            {run.avg_auto_score !== null && (
                              <div className="text-sm text-gray-600">
                                Auto Score: <span className="font-medium">{parseFloat(run.avg_auto_score).toFixed(2)}</span>
                                {run.manual_overrides > 0 && (
                                  <span className="ml-2 text-celadon-700">
                                    ({run.manual_overrides} overridden)
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(run.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => navigate(`/projects/${projectId}/test-sets/${testSetId}/evaluate/${run.prompt_version_id}`)}
                            className="btn-ghost btn-sm"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View Results
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Test Case Modal */}
      {showAddCaseForm && (
        <div className="modal-overlay">
          <div className="bg-white rounded-2xl shadow-strong max-w-3xl w-full max-h-[90vh] overflow-hidden animate-scale-in">
            {/* Header */}
            <div className="gradient-header border-b border-gray-100 px-8 py-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-celadon-100 rounded-xl">
                  <Plus className="h-6 w-6 text-celadon-700" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Add Test Case</h2>
                  <p className="text-gray-600 mt-1">Create a new test case for this test set</p>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-8">
              <form onSubmit={handleAddTestCase} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Input Text *
                  </label>
                  <textarea
                    value={newTestCase.inputText}
                    onChange={(e) => setNewTestCase({ ...newTestCase, inputText: e.target.value })}
                    placeholder="Enter the input that will be sent to the prompt..."
                    rows={4}
                    className="textarea"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Expected Behavior
                  </label>
                  <textarea
                    value={newTestCase.expectedBehavior}
                    onChange={(e) => setNewTestCase({ ...newTestCase, expectedBehavior: e.target.value })}
                    placeholder="Describe what the ideal output should be..."
                    rows={3}
                    className="textarea"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Tags (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={newTestCase.tags.join(', ')}
                    onChange={(e) => setNewTestCase({ 
                      ...newTestCase, 
                      tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
                    })}
                    placeholder="e.g., customer service, bug report, feature request"
                    className="input"
                  />
                </div>
              </form>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 bg-gray-50 px-8 py-6 rounded-b-2xl">
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddCaseForm(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddTestCase}
                  className="btn-primary"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Test Case
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Run Tests Modal */}
      {showRunTestsModal && (
        <div className="modal-overlay">
          <div className="bg-white rounded-2xl shadow-strong max-w-4xl w-full max-h-[95vh] overflow-hidden animate-scale-in">
            {/* Header */}
            <div className="gradient-header border-b border-gray-100 px-8 py-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-celadon-100 rounded-xl">
                  <Play className="h-6 w-6 text-celadon-700" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Run Tests</h2>
                  <p className="text-gray-600 mt-1">
                    Execute tests against {testSet.test_cases?.length} test cases
                  </p>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="overflow-y-auto custom-scrollbar max-h-[calc(95vh-140px)]">
              <div className="p-8 space-y-8">
                {/* Check if no test cases exist */}
                {(!testSet.test_cases || testSet.test_cases.length === 0) ? (
                  <div className="text-center py-16">
                    <div className="mx-auto w-24 h-24 bg-celadon-100 rounded-full flex items-center justify-center mb-6">
                      <FileText className="w-12 h-12 text-celadon-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">No test cases found</h3>
                    <p className="text-gray-600 mb-8 max-w-md mx-auto">
                      You need to add test cases before you can run tests. Test cases define the inputs and expected behaviors for prompt evaluation.
                    </p>
                    <div className="flex justify-center space-x-3">
                      <button
                        onClick={() => {
                          setShowRunTestsModal(false);
                          setShowAddCaseForm(true);
                        }}
                        className="btn-primary"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Test Case
                      </button>
                      <button
                        onClick={() => setShowRunTestsModal(false)}
                        className="btn-secondary"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Model Selection */}
                    <div className="card">
                      <div className="card-header">
                        <h3 className="text-lg font-semibold text-gray-900">Model Configuration</h3>
                        <p className="text-gray-600 mt-1">Choose the AI model for test execution</p>
                      </div>
                      <div className="card-body">
                        <select
                          value={selectedModel}
                          onChange={(e) => setSelectedModel(e.target.value)}
                          className="input"
                        >
                          {models.map((model) => (
                            <option key={model.id} value={model.id}>
                              {model.name} - {model.description}
                            </option>
                          ))}
                        </select>
                        <p className="text-sm text-gray-500 mt-3">
                          This model will execute all selected test cases
                        </p>
                      </div>
                    </div>

                {/* Test Case Selection */}
                <div className="card">
                  <div className="card-header">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Test Case Selection</h3>
                        <p className="text-gray-600 mt-1">Choose which test cases to execute</p>
                      </div>
                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={selectAllTests}
                          onChange={handleSelectAllToggle}
                          className="rounded border-gray-300 text-celadon-600 focus:ring-celadon-300"
                        />
                        <span className="text-sm font-medium text-gray-700">Run all tests</span>
                      </label>
                    </div>
                  </div>
                  <div className="card-body">
                    {!selectAllTests && (
                      <div className="max-h-60 overflow-y-auto custom-scrollbar bg-gray-50 rounded-xl p-4 border border-gray-200">
                        <div className="space-y-3">
                          {testSet.test_cases?.map((testCase, index) => (
                            <label key={testCase.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-white transition-colors cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedTestCases.includes(testCase.id)}
                                onChange={(e) => handleTestCaseSelection(testCase.id, e.target.checked)}
                                className="mt-1 rounded border-gray-300 text-celadon-600 focus:ring-celadon-300"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900">
                                  Test Case #{index + 1}
                                </div>
                                <div className="text-xs text-gray-500 truncate mt-1">
                                  {testCase.input_text.substring(0, 80)}...
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="mt-4 p-4 bg-celadon-50 border border-celadon-200 rounded-xl">
                      <p className="text-sm text-celadon-800">
                        {selectAllTests 
                          ? `All ${testSet.test_cases?.length || 0} test cases will be executed`
                          : selectedTestCases.length === 0 
                            ? 'Select test cases to run'
                            : `${selectedTestCases.length} test case(s) will be executed`
                        }
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Prompt Selection */}
                <div className="card">
                  <div className="card-header">
                    <h3 className="text-lg font-semibold text-gray-900">Prompt Versions</h3>
                    <p className="text-gray-600 mt-1">Select a prompt version to test</p>
                  </div>
                  <div className="card-body">
                    <div className="space-y-4 max-h-80 overflow-y-auto custom-scrollbar">
                      {prompts.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                          <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                          <p className="font-medium">No prompts found in this project.</p>
                          <p className="text-sm mt-1">Create a prompt first to run tests.</p>
                        </div>
                      ) : (
                        prompts.map((prompt) => (
                          <div key={prompt.id} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                            <h4 className="text-lg font-semibold text-gray-900 mb-4">{prompt.name}</h4>
                            <div className="space-y-3">
                              {!prompt.versions || prompt.versions.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                  <p className="text-sm">No versions found for this prompt.</p>
                                </div>
                              ) : (
                                prompt.versions.map((version) => (
                                  <div
                                    key={version.id}
                                    className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:shadow-soft transition-all duration-200"
                                  >
                                    <div>
                                      <div className="flex items-center space-x-2">
                                        <span className="font-semibold text-gray-900">v{version.version_number}</span>
                                        {version.is_active && (
                                          <span className="badge-status-success">
                                            Active
                                          </span>
                                        )}
                                      </div>
                                      {version.change_message && (
                                        <p className="text-sm text-gray-600 mt-1">{version.change_message}</p>
                                      )}
                                    </div>
                                    <button
                                      onClick={() => handleRunTests(prompt.id, version.id)}
                                      disabled={runningTests || (!selectAllTests && selectedTestCases.length === 0)}
                                      className="btn-primary"
                                    >
                                      {runningTests ? (
                                        <>
                                          <div className="spinner h-4 w-4 mr-2"></div>
                                          Running...
                                        </>
                                      ) : (
                                        <>
                                          <Play className="h-4 w-4 mr-2" />
                                          Run Tests
                                        </>
                                      )}
                                    </button>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
                  </>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 bg-gray-50 px-8 py-6 rounded-b-2xl">
              <div className="flex justify-end">
                <button
                  onClick={() => setShowRunTestsModal(false)}
                  disabled={runningTests}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Evaluation Settings Modal */}
      {showEvaluationSettings && (
        <EvaluationSettings
          testSetId={testSetId}
          onClose={() => setShowEvaluationSettings(false)}
        />
      )}
    </div>
  );
};

export default TestSetDetail; 